import React from 'react';
import { Box, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import { sbg } from '../../theme/sbgTokens';
import { ErrorState } from '../ui/EmptyState';
import { useDelayedFlag } from '../ui/useDelayedFlag';

/** A small inline fetch: 16px spinner beside its label (book §11.15). */
export const InlineLoading: React.FC<{ label: string }> = ({ label }) => (
  <Stack role="status" aria-label={label} direction="row" spacing={1.25} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
    <CircularProgress size={16} thickness={5} aria-hidden="true" />
    <Typography variant="body1" sx={{ color: sbg.text.secondary }}>{label}</Typography>
  </Stack>
);

/**
 * Whole-page loading: skeletons at the real layout (never a page spinner),
 * revealed after 300ms so fast loads don't flash.
 */
export const PageLoading: React.FC<{ label: string; variant?: 'table' | 'cards' | 'canvas' }> = ({ label, variant = 'table' }) => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box role="status" aria-label={label} aria-busy="true" sx={{ width: '100%', visibility: visible ? 'visible' : 'hidden' }}>
      <span className="sbg-sr-only">{label}</span>
      <Box aria-hidden="true">
        <Skeleton variant="rounded" width={96} height={14} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" width="32%" height={28} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" width="44%" height={16} sx={{ mb: 3 }} />
        {variant === 'cards' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            {[0, 1, 2, 3].map((n) => (
              <Skeleton key={n} variant="rounded" height={120} sx={{ borderRadius: sbg.radius.card }} />
            ))}
          </Box>
        )}
        {variant === 'canvas' ? (
          <Skeleton variant="rounded" height="min(55vh, 480px)" sx={{ borderRadius: sbg.radius.card }} />
        ) : (
          <Box sx={{ border: `1px solid ${sbg.border.default}`, bgcolor: sbg.bg.surface, borderRadius: sbg.radius.lg, overflow: 'hidden' }}>
            <Box sx={{ height: 36, bgcolor: sbg.bg.surfaceSubtle, borderBottom: `1px solid ${sbg.border.default}` }} />
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <Box key={n} sx={{ height: 40, display: 'flex', alignItems: 'center', gap: 2, px: 2, borderBottom: n < 5 ? `1px solid ${sbg.border.subtle}` : 0 }}>
                <Skeleton variant="rounded" width="24%" height={14} />
                <Skeleton variant="rounded" width="18%" height={14} />
                <Skeleton variant="rounded" width="12%" height={14} sx={{ ml: 'auto' }} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** Whole-page error: what happened, what to do now, with Retry. */
export const PageError: React.FC<{ title: string; description?: string; onRetry?: () => void }> = ({
  title,
  description = 'The server didn’t respond. Try again in a minute.',
  onRetry,
}) => (
  <Box sx={{ maxWidth: 560, mx: 'auto', mt: 4, bgcolor: sbg.bg.surface, border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.card }}>
    <ErrorState title={title} body={description} onRetry={onRetry} />
  </Box>
);
